import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked, cached } from '@glimmer/tracking';
import type { AxisLegends, AxisSummary, CircleCoordinate, HoverTextValues, HoverTitles } from '../index.d.ts';
import renderModifier from '../modifiers/aditools-renderer.ts';

type TextDimensions = {
  lineHeight: number;
  width: number;
  height: number;
};

interface AditoolsGraphHoverArgs {
  show: boolean;
  showSummary: boolean;
  chartHeight: number;
  lineClasses: string[];
  titles: HoverTitles;
  x: number;
  y: number;
  text: HoverTextValues;
  circles: CircleCoordinate[];
  hoverWidthAdjust?: number;
  hoverOnNoData?: boolean;
}

interface AditoolsGraphHoverSignature {
  Args: AditoolsGraphHoverArgs;
}

export default class AditoolsGraphHover extends Component<AditoolsGraphHoverSignature> {
  target?: SVGElement;
  @tracked swatchElement?: SVGElement;
  @tracked rendered = false;
  renderModifier = renderModifier;
  maxCharHeight = 18;
  estCharWidth = 7;
  swatchLabelText;
  swatchValueText;
  hoverWidthAdjust;
  lineWidths: number[] = [];

  @tracked displayBox = false;
  @tracked boxDimensions: Record<string, number> = {};

  constructor(owner: unknown, args: AditoolsGraphHoverArgs) {
    super(owner, args);
    this.swatchLabelText = 'Frequency: ';
    this.swatchValueText = '240.3 MHz';
    this.hoverWidthAdjust = this.args.hoverWidthAdjust ? this.args.hoverWidthAdjust : 1.05;
  }

  @action inserted(element: Element): void {
    this.target = element as SVGElement;
    this.rendered = true;
  }

  @action destroying(): void {
    this.target = undefined;
    this.rendered = false;
  }

  @action insertedSwatch(element: Element): void {
    if (element.parentElement) {
      this.swatchElement = element.parentElement as Node as SVGElement;
    }
  }

  @action destroyingSwatch(): void {
    this.swatchElement = undefined;
  }

  getDimensionsOfSwatch(
    textElements: SVGTextContentElement[],
    overridingLineHeight?: number
  ): Record<string, number[]> {
    const lines: Record<string, number[]> = {};
    textElements.forEach((element) => {
      const childNodes = element.querySelectorAll('tspan');
      if (childNodes[0] && childNodes[1]) {
        const labelElement = childNodes[0];
        const valueElement = childNodes[1];
        const labelRect = labelElement.getBBox();
        const labelText = labelElement.textContent ? labelElement.textContent : '';
        const valueRect = valueElement.getBBox();
        const valueText = valueElement.textContent ? valueElement.textContent : '';
        const labelWidth =
          labelRect.width > 1.5 * labelElement.textLength.baseVal.value && labelElement.textLength.baseVal.value !== 0
            ? labelElement.textLength.baseVal.value
            : labelRect.width;
        const valueWidth =
          valueRect.width > 1.5 * valueElement.textLength.baseVal.value && labelElement.textLength.baseVal.value !== 0
            ? valueElement.textLength.baseVal.value
            : valueRect.width;
        const name = element.classList.contains('focused') ? 'focused' : 'base';
        const labelCharWidth = labelWidth / labelText.length;
        const valueCharWidth = valueWidth / valueText.length;
        const lineHeight = overridingLineHeight ? overridingLineHeight : Math.max(labelRect.height, valueRect.height);
        lines[name] = [labelCharWidth, valueCharWidth, lineHeight];
      } else {
        throw 'Swatch label and value not found';
      }
    });
    return lines;
  }

  @cached
  get lineDimensions(): Record<string, number[]>[] {
    const lineDimensions = [];
    if (this.swatchElement) {
      const swatchParent = this.swatchElement.querySelector<SVGTextContentElement>(
        '.graph-hover_legends--swatch .hover-legend-text'
      );
      const swatchLines = this.swatchElement.querySelectorAll<SVGTextContentElement>(
        '.graph-hover_legends--swatch .hover-legend-text_row'
      );
      // The following calculation provides a heuristic to use instead of Safari's broken tspan bboxes - only fires on Safari
      const parentBox = swatchParent?.getBBox();
      const swatchBox = swatchLines[0]?.getBBox();
      const overridingHeight =
        parentBox?.height && swatchBox?.height === parentBox?.height
          ? (parentBox.height - 3 * swatchLines.length) / swatchLines.length
          : undefined;

      if (swatchLines && swatchLines.length !== 2 * this.args.lineClasses.length + 1) {
        console.log(
          `${swatchLines.length} swatches - with ${this.args.lineClasses.length} line classes, ` +
            `should be ${2 * this.args.lineClasses.length + 1} swatches`
        );
      }
      if (swatchLines && swatchLines.length > 0) {
        for (let i = 1; i < swatchLines.length; i += 2) {
          if (swatchLines[i] && swatchLines[i + 1]) {
            lineDimensions.push(
              this.getDimensionsOfSwatch(
                [
                  swatchLines[i] as SVGTextContentElement, //Covered by guard clause above
                  swatchLines[i + 1] as SVGTextContentElement,
                ],
                overridingHeight
              )
            );
          }
        }
        if (swatchLines[0]) {
          lineDimensions.push(this.getDimensionsOfSwatch([swatchLines[0]], overridingHeight));
        }
      }
    }
    return lineDimensions;
  }

  get legendTextDimensions(): TextDimensions {
    const lineWidths = [];
    const lineHeights = [];
    let lineDimension = [];
    let labelWidth = 0;
    let valueWidth = 0;
    let lineWidth = 0;
    let lineHeight = 0;
    const lineDimensions = this.lineDimensions;

    this.args.text.yAxisValues.forEach((yValue) => {
      const dimIndex = Math.min(yValue.index, lineDimensions.length - 2);
      const legend = lineDimensions[dimIndex];
      if (legend && legend['focused'] && legend['base']) {
        lineDimension = yValue.focused ? legend['focused'] : legend['base'];
        const labelHeight = lineDimension[0] ?? 0;
        const labelLength = this.args.titles.lineLabels[yValue.index] ?? '';
        labelWidth = labelLength.length * labelHeight;
        const valueHeight = lineDimension[1] ?? 0;
        valueWidth = yValue.displayValue.length * valueHeight;
        lineWidth = labelWidth + valueWidth;
        lineHeight = lineDimension[2] ?? 0;
        lineWidths.push(lineWidth);
        lineHeights.push(lineHeight);
      } else {
        throw 'legend not found';
      }
    });
    const legend = lineDimensions[lineDimensions.length - 1];
    if (legend && legend['base']) {
      lineDimension = legend['base'];
      const labelHeight = lineDimension[0] ?? 0;
      labelWidth = this.args.titles.headerLabel.length * labelHeight;
      const valueHeight = lineDimension[1] ?? 0;
      valueWidth = (this.args.text.xAxisValueWithUnit || '').length * valueHeight;
      lineWidth = labelWidth + valueWidth;
      lineHeight = lineDimension[2] ?? 0;
      lineWidths.push(lineWidth);
      lineHeights.push(lineHeight);
    } else {
      throw 'legend not found';
    }

    return {
      lineHeight: Math.max.call(undefined, ...lineHeights),
      width: Math.max.call(undefined, ...lineWidths) * this.hoverWidthAdjust + 10,
      height: lineHeights.length * Math.max.call(undefined, ...lineHeights) + 10,
    };
  }

  get xAxisSummary(): AxisSummary {
    const text =
      this.args.text.xAxisValue && this.args.text.xAxisValue !== null ? this.args.text.xAxisValue : 'X-Axis Value';
    const boxWidth = text.length * this.estCharWidth + 15;
    const boxLeftOffset = this.args.x - boxWidth / 2;
    const boxTopOffset = this.args.chartHeight + 2;
    return {
      chartHeight: this.args.chartHeight + 2,
      dx: boxWidth / 2,
      boxHeight: 20,
      boxLeftOffset: boxLeftOffset,
      boxTopOffset: boxTopOffset,
      boxWidth: boxWidth,
      text: text,
      x: this.args.x,
      y: this.args.y,
    };
  }

  get yAxisLegends(): AxisLegends {
    const dimensions = this.legendTextDimensions;
    const yValues = this.args.text.yAxisValues.map((yValue) => {
      return {
        index: yValue.index,
        displayValue: yValue.displayValue,
        focused: yValue.focused ? ' focused' : '',
      };
    });
    const legendCloseToLeft = this.args.x <= dimensions.width + 10;
    const legendCloseToTop = this.args.y > this.args.chartHeight - (dimensions.height + 10);

    return {
      dy: dimensions.lineHeight,
      boxHeight: dimensions.height,
      boxWidth: dimensions.width,
      boxLeftOffset: legendCloseToLeft ? this.args.x + 10 : this.args.x - (dimensions.width + 10),
      boxTopOffset: legendCloseToTop ? this.args.chartHeight - (dimensions.height + 10) : this.args.y + 10,
      values: yValues,
      x: this.args.x,
      y: this.args.y,
    };
  }

  get showHover(): boolean {
    const hoverOnNoData = this.args.hoverOnNoData !== undefined ? this.args.hoverOnNoData : true;
    return hoverOnNoData ? this.args.show : this.args.show && this.yAxisLegends.values.length > 0;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    AditoolsGraphHover: typeof AditoolsGraphHover;
  }
}
